// Difficulty-neutral opponents for the test build. Each level receives ONLY
// the board and its disc colour, computes legal moves through the engine,
// and returns one legal coordinate. It never sees UI state.

import {
  type Board,
  type Disc,
  DARK,
  legalMoves,
  hasLegalMove,
  flipsFor,
  applyMove,
  opponent,
  score,
} from "./othello";

// 1–2 are the original hand-tuned early-game opponents (린/브람) — unchanged.
// 3–6 select a minimax search depth (see chooseSearch below): the campaign's
// difficulty curve from 토르 onward is "how many plies ahead can this
// opponent see", not a different heuristic per level.
export type AiLevel = 1 | 2 | 3 | 4 | 5 | 6;

function pick<T>(list: T[], rng: () => number): T {
  return list[Math.floor(rng() * list.length)];
}

// Stage 1 — 린, 이끼 문지기: "기본 수 위주". Almost always a plain random
// legal move, occasionally the most-flips move. Deliberately weak so a new
// player can win their first match.
function chooseLevel1(board: Board, me: Disc, rng: () => number): number {
  const moves = legalMoves(board, me);
  if (rng() < 0.25) {
    let best = moves[0];
    let bestCount = -1;
    for (const m of moves) {
      const c = flipsFor(board, m, me).length;
      if (c > bestCount) { bestCount = c; best = m; }
    }
    return best;
  }
  return pick(moves, rng);
}

// The four corners' diagonal (X) and orthogonal (C) neighbours — the cells that
// most often hand a corner to the opponent.
const DANGER = new Set([
  1, 8, 9, // near corner 0
  6, 14, 15, // near corner 7
  48, 49, 57, // near corner 56
  54, 55, 62, // near corner 63
]);

// Stage 2 — 브람, 등불 수집가: "위험 칸 약하게 회피". Plain random-legal like
// level 1, except roughly half the time it steers off the corner-danger cells
// when a safer move exists. Notably tougher than level 1, still beatable with
// corner play.
function chooseLevel2(board: Board, me: Disc, rng: () => number): number {
  const moves = legalMoves(board, me);
  const safe = moves.filter((m) => !DANGER.has(m));
  const pool = safe.length > 0 && rng() < 0.45 ? safe : moves;
  return pick(pool, rng);
}

/* ------------------------- levels 3–6: minimax ------------------------- *
 * 토르 (stage 4) onward all used to share one 1-ply "leave the opponent the
 * fewest replies" heuristic — every later opponent, boss or not, played
 * identically. This replaces it with a real negamax + alpha-beta search: the
 * `level` (3–6) IS the ply depth, so the campaign's later/boss fights (칼데르
 * ·로웬·카이로스·벨라) can look further ahead than the stage right before
 * them — see stages.ts for the per-stage assignment. Search cost is bounded
 * by depth (≤6) and alpha-beta pruning; a game.test.ts benchmark keeps an eye
 * on worst-case move time. */

// Classic Othello positional weight table (corners very strong, the cells
// next to an empty corner strongly negative, edges good, interior mild) —
// used both as the leaf-node positional term and to order moves so
// alpha-beta prunes effectively (corners/edges searched first).
const WEIGHTS = [
  120, -20, 20, 5, 5, 20, -20, 120,
  -20, -40, -5, -5, -5, -5, -40, -20,
  20, -5, 15, 3, 3, 15, -5, 20,
  5, -5, 3, 3, 3, 3, -5, 5,
  5, -5, 3, 3, 3, 3, -5, 5,
  20, -5, 15, 3, 3, 15, -5, 20,
  -20, -40, -5, -5, -5, -5, -40, -20,
  120, -20, 20, 5, 5, 20, -20, 120,
];

// Score a board from `side`'s point of view (higher = better for `side`):
// positional weights + mobility (matters more early/mid) + disc count
// (matters more late) — the classic phase-blended Othello heuristic.
function evaluate(board: Board, side: Disc): number {
  const foe = opponent(side);
  let posScore = 0;
  for (let i = 0; i < 64; i++) {
    if (board[i] === side) posScore += WEIGHTS[i];
    else if (board[i] === foe) posScore -= WEIGHTS[i];
  }
  const myMoves = legalMoves(board, side).length;
  const foeMoves = legalMoves(board, foe).length;
  const s = score(board);
  const phase = (64 - s.empty) / 64; // 0 opening -> 1 endgame
  const mobilityScore = (myMoves - foeMoves) * (15 * (1 - phase));
  const myDiscs = side === DARK ? s.dark : s.light;
  const foeDiscs = side === DARK ? s.light : s.dark;
  const discScore = (myDiscs - foeDiscs) * (10 * phase);
  return posScore + mobilityScore + discScore;
}

// Search corners/edges first so alpha-beta cuts off the weak branches sooner.
function orderMoves(moves: number[]): number[] {
  return moves.slice().sort((a, b) => WEIGHTS[b] - WEIGHTS[a]);
}

// Negamax with alpha-beta pruning. Returns the value of `board` from the
// player-to-move (`side`)'s perspective. A side with no legal move passes
// (still spends a ply, per the engine's own nextTurn/checkEnd rules) rather
// than ending the search early — only a genuine double-stuck position (or
// depth 0) is a leaf.
function negamax(
  board: Board,
  depth: number,
  alpha: number,
  beta: number,
  side: Disc,
): number {
  const foe = opponent(side);
  if (depth <= 0) return evaluate(board, side);

  const moves = legalMoves(board, side);
  if (moves.length === 0) {
    if (!hasLegalMove(board, foe)) return evaluate(board, side);
    return -negamax(board, depth - 1, -beta, -alpha, foe);
  }

  let best = -Infinity;
  for (const m of orderMoves(moves)) {
    const { board: next } = applyMove(board, m, side);
    const val = -negamax(next, depth - 1, -beta, -alpha, foe);
    if (val > best) best = val;
    if (best > alpha) alpha = best;
    if (alpha >= beta) break; // beta cutoff
  }
  return best;
}

function chooseSearch(board: Board, me: Disc, depth: number, rng: () => number): number {
  const foe = opponent(me);
  const moves = legalMoves(board, me);
  let alpha = -Infinity;
  const beta = Infinity;
  let bestScore = -Infinity;
  let bestMoves: number[] = [];
  for (const m of orderMoves(moves)) {
    const { board: next } = applyMove(board, m, me);
    const val = -negamax(next, depth - 1, -beta, -alpha, foe);
    if (val > bestScore) {
      bestScore = val;
      bestMoves = [m];
      alpha = val;
    } else if (val === bestScore) {
      bestMoves.push(m);
    }
  }
  return pick(bestMoves, rng);
}

export function chooseMove(
  level: AiLevel,
  board: Board,
  me: Disc,
  rng: () => number = Math.random,
): number {
  const moves = legalMoves(board, me);
  if (moves.length === 0) throw new Error("chooseMove called with no legal move");
  switch (level) {
    case 1:
      return chooseLevel1(board, me, rng);
    case 2:
      return chooseLevel2(board, me, rng);
    default:
      return chooseSearch(board, me, level, rng);
  }
}
